import { rem } from 'polished';
import Box from './Box/Box';
import { css } from 'styled-components';
import { Body1, Body2, Headline } from './Typography/Typography';
import { theme } from '../../config/theme';
import React, { useEffect, useState } from 'react';
import Input from './Input/Input';
// @ts-ignore
import SearchIcon from '../images/icons/search.svg';
import useViewport from '../hooks/useViewport';
import { Link } from 'gatsby';

const SearchBox = (props) => {
    return (
        <Box
            as={Input}
            placeholder="Search"
            endIcon={<Box as={SearchIcon} size={rem(20)} />}
            {...props}
        />
    );
};

const CategoryButton = ({
    isTagPage = false,
    isBlogPage = false,
    isAllButton = false,
    filter = null,
    category,
    isMobile,
    filterByCategory = () => {},
    activeTab,
    setActiveTab = () => {},
    listContainerId,
}) => {
    const getJSX = React.useCallback(
        (categoryName) => (
            <Box
                as={isMobile ? Headline : Body2}
                color={{
                    _: theme.colors.primary[1700],
                    sm: theme.colors.primary[1600],
                }}
                // @ts-ignore
                css={css`
                    display: flex;
                    user-select: none;
                    ${isMobile
                        ? css`
                              justify-content: center;
                              border: 2px solid ${theme.colors.primary[1700]};
                              border-radius: ${theme.radii.full};
                              ${activeTab === categoryName &&
                              css`
                                  color: ${theme.colors.white};
                                  background-color: ${theme.colors
                                      .primary[1700]};
                              `}
                          `
                        : css`
                              ${categoryName === activeTab &&
                              css`
                                  font-weight: bold;
                                  color: ${theme.colors.primary.main};
                              `}
                              :hover {
                                  color: ${theme.colors.primary.main};
                                  text-decoration: underline;
                              }

                              + * {
                                  margin-top: ${rem(4)};
                              }
                          `}
                `}
                paddingY={{ _: rem(12), sm: rem(4) }}
                paddingX={{ _: rem(17), sm: 0 }}
                cursor="pointer"
                whiteSpace={{ _: 'nowrap', sm: 'initial' }}
                onClick={() => {
                    filterByCategory(category);
                    setActiveTab?.(categoryName);
                }}
            >
                {categoryName}
            </Box>
        ),
        [filterByCategory, setActiveTab, category, isMobile]
    );

    if (isBlogPage || isTagPage) {
        return (
            <Link
                to={
                    isAllButton
                        ? `/${isBlogPage ? 'blog' : ''}#${listContainerId}`
                        : filter?.path
                        ? filter.path + `#${listContainerId}`
                        : `/${isBlogPage ? 'blog' : ''}/tag/${
                              category.slug
                          }#${listContainerId}`
                }
            >
                {getJSX(isAllButton || filter?.name ? category : category.name)}
            </Link>
        );
    }

    return (
        <Box as={Link} to={`#${listContainerId}`}>
            {getJSX(category)}
        </Box>
    );
};

export const CardListSidebar = ({
    isBlogPage = false,
    isTagPage = false,
    categoryNameList,
    allItems,
    filterByCategory = () => {},
    activeTab,
    setActiveTab,
    setItems,
    setCurrentClickedItem,
    sidebarTitle1,
    sidebarTitle2,
    sidebarOption1,
    setHidePagination,
    currentPageItems,
    filters,
    listContainerId,
}) => {
    const [hideFilterTabs, setHideFilterTabs] = useState(false);
    const { isMobile } = useViewport();
    const FilterSectionComponent = isMobile ? React.Fragment : Box;

    const handleOnChange = (e) => {
        setCurrentClickedItem?.(null);
        const input = e.target.value;

        if (input.length > 1) {
            setHidePagination?.(true);
            setHideFilterTabs(true);
            const filteredItems = allItems.filter((item) =>
                (isTagPage || isBlogPage ? item.data.title : item.name)
                    .toLowerCase()
                    .includes(input.toLowerCase())
            );
            setItems(filteredItems);
        }

        if (input.length === 0) {
            setItems(currentPageItems ? currentPageItems : allItems);
            setHidePagination?.(false);
            setHideFilterTabs(false);
        }
    };

    return (
        <Box
            gridColumn={{
                _: '1/-1',
                sm: 'span 2',
                ...((isTagPage || isBlogPage) && {
                    md: '1 / span 2',
                }),
                lg: `2 / span 2`,
            }}
            gridRow="1/-1"
            maxWidth={{ _: 'auto', sm: rem(178) }}
            alignSelf="start"
            position={{ _: 'static', lg: 'sticky' }}
            top="calc(var(--header-height) + 20px)"
        >
            <Box>
                <Box marginBottom={rem(isMobile ? 36 : 16)}>
                    <SearchBox onChange={handleOnChange} />
                </Box>
                {isMobile && hideFilterTabs ? null : (
                    <Box
                        display={{ _: 'grid', sm: 'block' }}
                        gridAutoFlow={{ _: 'column', sm: 'initial' }}
                        gridGap={{ _: rem(8), sm: 'initial' }}
                        // @ts-ignore
                        css={
                            isMobile
                                ? css`
                                      overflow-x: auto;
                                      width: calc(100% + ${rem(32)});
                                      margin-left: ${rem(-16)};
                                      padding-inline: ${rem(16)};
                                      padding-bottom: ${rem(8)};
                                  `
                                : css`
                                      > * + * {
                                          margin-top: ${rem(16)};
                                      }
                                  `
                        }
                    >
                        <FilterSectionComponent>
                            {isMobile ? null : (
                                <Box
                                    as={Headline}
                                    paddingY={rem(8)}
                                    mb={rem(4)}
                                >
                                    {sidebarTitle1}
                                </Box>
                            )}
                            <CategoryButton
                                isAllButton
                                isTagPage={isTagPage}
                                isBlogPage={isBlogPage}
                                category={sidebarOption1}
                                isMobile={isMobile}
                                activeTab={activeTab}
                                filterByCategory={(category) => {
                                    if (isBlogPage) {
                                        setItems(allItems);
                                    } else {
                                        filterByCategory(category);
                                    }
                                }}
                                setActiveTab={setActiveTab}
                                listContainerId={listContainerId}
                            />
                            {filters?.map((filter) => (
                                <CategoryButton
                                    isTagPage={isTagPage}
                                    isBlogPage={isBlogPage}
                                    category={filter.name}
                                    isMobile={isMobile}
                                    activeTab={activeTab}
                                    setActiveTab={setActiveTab}
                                    filter={filter}
                                    listContainerId={listContainerId}
                                />
                            ))}
                        </FilterSectionComponent>
                        <FilterSectionComponent>
                            {isMobile ? null : (
                                <Box
                                    as={Headline}
                                    paddingY={rem(8)}
                                    mb={rem(4)}
                                >
                                    {sidebarTitle2}
                                </Box>
                            )}
                            {categoryNameList.map((category) => (
                                <CategoryButton
                                    isTagPage={isTagPage}
                                    isBlogPage={isBlogPage}
                                    category={category}
                                    isMobile={isMobile}
                                    activeTab={activeTab}
                                    filterByCategory={filterByCategory}
                                    setActiveTab={setActiveTab}
                                    listContainerId={listContainerId}
                                />
                            ))}
                        </FilterSectionComponent>
                    </Box>
                )}
            </Box>
        </Box>
    );
};
